// ── Copy text to the clipboard, everywhere ───────────────────────────────────
// `navigator.clipboard.writeText(x).then(...)` was used directly in ten places.
// Two problems with that:
//
//   1. When `navigator.clipboard` is undefined the property access throws a
//      SYNCHRONOUS TypeError, so a trailing `.catch()` never runs — the error
//      escapes into the click handler and React's error boundary shows
//      "Something went wrong". It is undefined in any non-secure context and in
//      several in-app browsers (Facebook/Instagram), which is exactly where
//      referral and affiliate links get opened.
//
//   2. Callers set their "Copied!" state optimistically, so the confirmation
//      appeared even when the copy had failed and the clipboard was untouched.
//
// This helper never throws, falls back to the execCommand textarea trick for
// older WebViews, and returns whether the copy actually happened so callers can
// tell the truth.
export const copyToClipboard = async (text) => {
  const value = String(text ?? '');
  if (!value) return false;

  // Preferred path — available in secure contexts on modern browsers.
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // Permission denied, document not focused, or an in-app browser that
    // exposes the API but refuses it. Fall through to the legacy path.
  }

  // Legacy fallback: a hidden textarea + execCommand('copy'). Deprecated, but
  // it is the only thing that works in older Android WebViews.
  try {
    const ta = document.createElement('textarea');
    ta.value = value;
    // Keep it off-screen and non-disruptive: readOnly stops the mobile keyboard
    // opening, and position:fixed avoids scrolling the page on focus.
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-9999px';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, value.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return !!ok;
  } catch {
    return false;
  }
};

export default copyToClipboard;
